// Admin service for managing users, roles, and permissions
import { buildApiUrl, debugLog } from '../config/environment';
import { getAuthToken } from './authService';

const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const handleResponse = async (response, errorMessage) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || errorMessage);
  }
  return response.json();
};

// ─── Users ────────────────────────────────────────────────────────────────────

export const getUsers = async () => {
  debugLog('Admin: fetching users');
  const response = await fetch(buildApiUrl('/admin/users'), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to fetch users');
};

export const getUser = async (userId) => {
  debugLog('Admin: fetching user', userId);
  const response = await fetch(buildApiUrl(`/admin/users/${userId}`), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to fetch user');
};

export const assignRoleToUser = async (userId, roleId) => {
  debugLog('Admin: assigning role', roleId, 'to user', userId);
  const response = await fetch(buildApiUrl(`/admin/users/${userId}/roles`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ role_id: roleId }),
  });
  return handleResponse(response, 'Failed to assign role');
};

export const removeRoleFromUser = async (userId, roleId) => {
  debugLog('Admin: removing role', roleId, 'from user', userId);
  const response = await fetch(buildApiUrl(`/admin/users/${userId}/roles/${roleId}`), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to remove role');
};

// ─── Roles ────────────────────────────────────────────────────────────────────

export const getRoles = async () => {
  debugLog('Admin: fetching roles');
  const response = await fetch(buildApiUrl('/admin/roles'), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to fetch roles');
};

export const addPermissionToRole = async (roleId, permissionId) => {
  debugLog('Admin: adding permission', permissionId, 'to role', roleId);
  const response = await fetch(buildApiUrl(`/admin/roles/${roleId}/permissions`), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ permission_id: permissionId }),
  });
  return handleResponse(response, 'Failed to add permission');
};

export const removePermissionFromRole = async (roleId, permissionId) => {
  debugLog('Admin: removing permission', permissionId, 'from role', roleId);
  const response = await fetch(buildApiUrl(`/admin/roles/${roleId}/permissions/${permissionId}`), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to remove permission');
};

// ─── Permissions ──────────────────────────────────────────────────────────────

export const getPermissions = async () => {
  debugLog('Admin: fetching permissions');
  const response = await fetch(buildApiUrl('/admin/permissions'), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse(response, 'Failed to fetch permissions');
};

